import { useState, useEffect } from "react";
import { styled, Checkbox } from "@mui/material";
import { Typography as T } from "@mui/material";
import { Link } from "react-router-dom";
import Container from "../../../components/PageContainer";
import InnerContainer from "../../../components/PageInnerContainer";
import PageTitle from "../../../components/PageTitle";
import PageSubTitle from "../../../components/PageSubTitle";
import Button from "../../../components/Button";
import ContentBox, {
  ContentHeader,
  ContentSubHeader,
} from "../../../components/ContentBox";
import {
  Section,
  SectionFlexColumn,
  SectionFlexRow,
} from "../../../components/General";
import { colors } from "../../../styles";
import Switch from "../../../components/Switch";
import Slider from "../../../components/Slider";
import { StepComponentProps } from "./index";
import { ContentTitle } from "./SavingsProgram/ProgramDefinition";
import useGetOnboarding from "../../../hooks/queries/admin/useGetOnboarding";
import useSaveOnboarding from "../../../hooks/mutations/admin/useSaveOnboarding";
import checkmark_green from "../../../assets/icons/checkmark_green.svg";
import star from "../../../assets/icons/star.svg";
import { ReactComponent as PhoneIcon } from "../../../assets/icons/phoneIcon.svg";
import { ReactComponent as ApproveIcon } from "../../../assets/icons/approve_icon.svg";
import { useStoreActions } from "../../../store";

const InnerContent = styled(`div`)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PricingBreakdown = styled(`div`)`
  display: flex;
  width: 100%;
`;

const PriceSection = styled(`div`)`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Price = styled(`h2`)`
  font-size: 32px;
  margin: 0px;
`;

const ListOfFeatures = styled(`div`)`
  display: flex;
  flex-direction: column;
  flex: 1.75;
  padding-left: 24px;
  border-left: 1px solid #e4e7ec;
`;

const FeatureItem = styled(`div`)`
  display: flex;
`;

const FeatureItemContent = styled(`p`)`
  font-size: 14px;
  margin: 8px 0px;
`;

const FeatureIcon = styled("img")`
  margin-right: 12px;
`;

const AddOnSection = styled(`div`)`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const AddOnTitle = styled(`h3`)`
  font-size: 16px;
`;

const AddOnItem = styled(`div`)`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const StarIcon = styled("img")`
  padding-left: 12px;
  padding-right: 22px;
`;

const MonthlyEstimate = styled(`p`)`
  font-size: 12px;
  margin: 0px;
`;

const featureItems = [
  "Month-to-month. Cancel anytime.",
  "Dedicated support for onboarding and maintenance",
  "Great employee experience with web and mobile app for account access",
  "Custom marketing materials to increase adoption",
  "Reporting and analytics on program performance",
];

const Pricing = ({ nextStep }: StepComponentProps) => {
  const modalActions = useStoreActions((actions) => actions.modals);
  const [numForEstimate, setNumForEstimate] = useState(0);
  const [agreedToSavingsTerms, setAgreedToSavingsTerms] = useState(false);

  const { data } = useGetOnboarding();
  const { mutate: saveOnboarding } = useSaveOnboarding({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const valueToText = (value: number) => {
    return `${value.toLocaleString()}`;
  };

  if (data) {
    const pppmPrice = data.program.offer_debit_card ? "4.50" : "3.00";

    return (
      <Container>
        <PageTitle>Pricing that works for you</PageTitle>
        <PageSubTitle>
          We've simplified pricing so you can focus your budget on helping your
          employees build their financial lives.
        </PageSubTitle>
        <PageSubTitle style={{ fontSize: 12 }}>
          Note: This is the administrative pricing for running and maintaining
          the program and does not include matches to participants.
        </PageSubTitle>
        <InnerContainer style={{ paddingBottom: 150 }}>
          <ContentBox>
            <ContentHeader>Standard Pricing</ContentHeader>
            <InnerContent>
              <PricingBreakdown>
                <PriceSection>
                  <Price>${pppmPrice}</Price>
                  <FeatureItemContent style={{ marginTop: 0 }}>
                    Per Participant Per Month (pppm)
                  </FeatureItemContent>
                </PriceSection>
                <ListOfFeatures>
                  {featureItems.map((item, index) => (
                    <FeatureItem key={index}>
                      <FeatureIcon src={checkmark_green} />
                      <FeatureItemContent>{item}</FeatureItemContent>
                    </FeatureItem>
                  ))}
                </ListOfFeatures>
              </PricingBreakdown>
              <AddOnSection>
                <AddOnTitle>Add-ons</AddOnTitle>
                <AddOnItem>
                  <Switch
                    checked={data.program.offer_debit_card}
                    onClick={(e) =>
                      saveOnboarding({
                        programItems: {
                          offer_debit_card: !data.program.offer_debit_card,
                        },
                      })
                    }
                  />
                  <FeatureItemContent>
                    Employee debit cards (+$1.50 pppm)
                  </FeatureItemContent>
                </AddOnItem>
                <AddOnItem>
                  <StarIcon src={star} />
                  <FeatureItemContent>
                    Custom matching rules are available <br />
                    <span
                      style={{ color: "#085cd7", cursor: "pointer" }}
                      onClick={() =>
                        modalActions.setShowContactSalesModal(true)
                      }
                    >
                      Contact sales
                    </span>{" "}
                    to learn more
                  </FeatureItemContent>
                </AddOnItem>
              </AddOnSection>
            </InnerContent>
          </ContentBox>
          <ContentBox>
            <ContentHeader>Pricing Estimate</ContentHeader>
            <ContentSubHeader style={{ fontSize: 14 }}>
              This is not how much you are committing to pay each month. It is
              an estimate. The actual amount will be based on signed up
              participants.
            </ContentSubHeader>
            <Section style={{ marginTop: 16 }}>
              <ContentTitle>
                Select Estimated Participating Employees
              </ContentTitle>
              <Slider
                min={0}
                max={data.employer.total_number_of_employees}
                valueLabelFormat={valueToText}
                getAriaValueText={valueToText}
                onChange={(e) =>
                  setNumForEstimate(
                    Number((e.target as HTMLInputElement)?.value)
                  )
                }
              />
            </Section>
            <SectionFlexColumn>
              <Price>
                ${(numForEstimate * Number(pppmPrice)).toLocaleString()}
              </Price>
              <MonthlyEstimate>Monthly Estimate</MonthlyEstimate>
            </SectionFlexColumn>
          </ContentBox>
          <SectionFlexRow style={{ justifyContent: "center", marginTop: 24 }}>
            <Checkbox
              checked={agreedToSavingsTerms}
              onClick={() => setAgreedToSavingsTerms(!agreedToSavingsTerms)}
            />
            <T variant="mdReg">
              Agree to{" "}
              <Link to={"savings-program-agreement"} color={colors.info_blue}>
                Savings Program Agreement
              </Link>
            </T>
          </SectionFlexRow>
          <SectionFlexRow style={{ marginTop: 30 }}>
            <Button
              onClick={() => modalActions.setShowContactSalesModal(true)}
              style={{
                marginTop: 0,
                marginRight: 8,
              }}
              btntype="secondary"
              startIcon={<PhoneIcon />}
            >
              Contact Sales
            </Button>
            <Button
              onClick={nextStep}
              style={{
                marginTop: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              disabled={!agreedToSavingsTerms}
            >
              <ApproveIcon /> <span style={{ marginLeft: 3 }}>Approve</span>
            </Button>
          </SectionFlexRow>
          <T variant="smReg" style={{ textAlign: "center", marginTop: 24 }}>
            By clicking the button, you agree to our Terms of Service and have
            read and acknowledge our Privacy Policy.
          </T>
        </InnerContainer>
      </Container>
    );
  }
};

export default Pricing;
